<template>
    <v-flex>
        <v-layout
            wrap
            class="attached-items"
            v-if="attachments && attachments.length > 0"
            data-qe-id="already-attached-files_list_area">
            <v-flex v-for="(file, key, index) in attachments" :key="key" class="attached-items__items">
                <i :class="iconClassNames(file.name)" class="attached-items__items__icon"></i>
                <span class="attached-items__items__title" :title="file.name">
                    <a :href="file.url" target="blank"><strong :data-qe-id="`attached_file_${index}_name`">{{file.name}}</strong></a>
                </span>
                <i v-if="!disabled" class="fas fa-times attached-items__items__close" :data-qe-id="`remove_attach_file_${index}_name`" @click="removeFile(file.name, key)"></i>
            </v-flex>
        </v-layout>
        <v-layout v-if="!disabled">
            <file-upload
                class="card__file-upload"
                :disabled="uploadInProgress"
                :title="fileAttachmentConsts.ATTACH_FILE"
                v-model="files"
                :multiple="multiple"
                input-id="waiver_file_attachment"
                data-qe-id="waiver_file_attachment">
                <i class="fas fa-plus-circle" data-qe-id = "btn_form_waiver_attachdoc"></i>
                <span>{{fileAttachmentConsts.WAIVER_ATTACH_FILE}}</span></file-upload>
                <v-progress-circular v-if="uploadInProgress" indeterminate color="grey" class="attach-documents__file-upload-spinner"></v-progress-circular>
        </v-layout>
        <v-flex v-if="(showAttachRequired && !errorMessage) || errorMessage" class="error-message mx-0">
            <span v-if="showAttachRequired && !errorMessage">{{fileAttachmentConsts.ATTACH_REQ}}</span>
            <span v-if="errorMessage">{{errorMessage}}</span>
        </v-flex>
        <v-flex v-if="waiverAttachmentSaved" class="success-message mx-0">
            <span>{{chapterWaiverConstants.ATTACHMENT_SUCCESS}}</span>
        </v-flex>
    </v-flex>
</template>

<script>
    import FileUpload from "vue-upload-component";
    import uniqBy from 'lodash/uniqBy';
    import { iconClassNames, communityCommonConstant, calculateFileSize } from "../ChapterPage/communityCommon.constant";
    import { fileAttachmentConsts } from '../ChapterPage/FileAttachment/FileAttachmentconstants';
    import { mapActions, mapGetters, mapMutations } from "vuex";
    import { storeAction } from "../../store/storeActions.constant";
    import { storeGetters } from "../../store/storeGetters.constant";
    import { storeMutation } from "../../store/storeMutations.constant";
    import { appCommonConstants } from "../appCommon.constants";
    import { chapterWaiverConstants } from "./ChapterWaiver.constants";

    export default {
        name: 'FileAttachments',
        props: {
            leadId: {
                type: String,
                required: true,
            },
            maxFilesToAttach: {
                type: Number,
                default: appCommonConstants.ONE,
            },
            maxFileSizeLimit: {
                type: Number,
                default: appCommonConstants.FILE_ATTACHMENT_SIZE_LIMIT, // 25 MB
            },
            attachments: {
                type: Array,
                default: () => [],
            },
            viewType: {
                type: String,
                required: true,
            },
            showAttachRequired: {
                type: Boolean,
                default: false,
            },
            attachmentCallback: {
                type: Function,
                required: true,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
        },
        data: () => {
            return {
                files: [],
                communityCommonConstant,
                appCommonConstants,
                errorMessage: '',
                fileAttachmentConsts,
                fileTypesSupported: fileAttachmentConsts.FILETYPES_SUPPORTED,
                uploadInProgress: false,
                attachmentsUploaded: false,
                chapterWaiverConstants,
            };
        },
        components: {
            FileUpload,
        },
        methods: {
            ...mapActions({
                uploadWaiverFiles: `chapterWaiver/${storeAction.uploadWaiverFiles}`,
                deleteWaiverFiles: `chapterWaiver/${storeAction.deleteWaiverFiles}`,
                getWaiverDetails: `chapterWaiver/${storeAction.getWaiverDetails}`,
            }),
            ...mapMutations({
                setWaiverAttachmentSaved: `chapterWaiver/${storeMutation.setWaiverAttachmentSaved}`,
            }),
            iconClassNames,
            calculateFileSize,
            removeFile(fileName, key) {
                const params = {
                    leadId: this.leadId,
                    fileName,
                };
                this.uploadInProgress = true;
                this.deleteWaiverFiles(params).then(() => {
                    this.uploadInProgress = false;
                    this.attachments.splice(key, 1);
                });
            },
            validateFileSize(files) {
                const bytes = files.reduce((size, file) => {
                    return size + file.size;
                }, 0);
                return bytes < this.maxFileSizeLimit;
            },
            validateDuplicateName(files) {
                return uniqBy(files, 'name').length === files.length;
            },
            validateFileType(files) {
                return files.every((file) => {
                    const type = file.name.substring(file.name.lastIndexOf('.') + 1);
                    return type && new RegExp(type).test(this.fileTypesSupported.join("|"));
                });
            },
            validateFiles(newFiles) {
                if (this.attachments.length + newFiles.length > this.maxFilesToAttach) {
                    this.errorMessage = `${fileAttachmentConsts.MAX_FILE_COUNT_EXCEEDED}(${this.maxFilesToAttach})`;
                    setTimeout(() => { this.errorMessage = ''; }, fileAttachmentConsts.ERROR_MESSAGE_TIMEOUT_SECONDS);
                    return false;
                } else if (!this.validateFileType([...newFiles])) {
                    this.errorMessage = fileAttachmentConsts.FILE_NOT_SUPPORTED;
                    setTimeout(() => { this.errorMessage = ''; }, fileAttachmentConsts.ERROR_MESSAGE_TIMEOUT_SECONDS);
                    return false;
                } else if (!this.validateDuplicateName([...this.attachments, ...newFiles])) {
                    this.errorMessage = fileAttachmentConsts.FILENAME_ALREADY_EXISTS;
                    setTimeout(() => { this.errorMessage = ''; }, fileAttachmentConsts.ERROR_MESSAGE_TIMEOUT_SECONDS);
                    return false;
                } else if (!this.validateFileSize([...newFiles])) {
                    this.errorMessage = fileAttachmentConsts.FILE_SIZE_EXCEEDED;
                    setTimeout(() => { this.errorMessage = ''; }, fileAttachmentConsts.ERROR_MESSAGE_TIMEOUT_SECONDS);
                    return false;
                }
                return true;
            },
        },
        computed: {
            multiple() {
                return this.maxFilesToAttach > 1;
            },
            ...mapGetters({
                waiverAttachmentSaved: `chapterWaiver/${storeGetters.waiverAttachmentSaved}`,
            }),
        },
        watch: {
            files(arr) {
                if (arr && arr.length) {
                    if (this.validateFiles(arr)) {
                        const formData = new FormData();
                        for (let i = 0; i < arr.length; i += 1) {
                            formData.append(`Files[${i}]`, arr[i].file);
                        }
                        const postData = {
                            leadId: this.leadId,
                            formData,
                        };
                        this.uploadInProgress = true;
                        this.uploadWaiverFiles(postData).then(() => {
                            this.uploadInProgress = false;
                            this.attachments.push(...arr);
                            this.attachmentCallback(arr);
                            if (this.waiverAttachmentSaved) {
                                setTimeout(() => {
                                    this.setWaiverAttachmentSaved(false);
                                }, 3000);
                            }
                        });
                    }
                    this.files = [];
                }
            },
        },
    };
</script>

<style lang="scss">
    @import "../../common/styles/_colors.scss";
    $image_icon_color:$ypo-duskblue;

    .success-message {
            color: $ypo-medium-green;
            margin: 8px;
    }
    .attached-items {
        &__items {
            border: 1px $ypo-cta dashed;
            position: relative;
            color: $ypo-cta;
            text-align: left;
            padding: 8px 10px;
            max-width: 150px;
            margin: 20px 10px 0 0;

            &__title {
                text-overflow: ellipsis;
                overflow: hidden;
                font-size: 12px;
                white-space: nowrap;
                display: inline-block;
                padding: 0 0 0 40px;
                width: 100%;
                border: 0;
                line-height: normal;
                vertical-align: middle;
                font-size: normal;
            }

            &__icon {
                font-size: 22px;
                position: absolute;
                left: 10px
            }

            &__close {
                position: absolute;
                right: -5px;
                top: -9px;
                background-color: $ypo-cta;
                color: $white;
                width: 16px;
                height: 16px;
                line-height: 16px !important;
                font-size: 10px;
                border-radius: 50px;
                cursor: pointer;
                text-align: center;
            }
        }

        .file-attachment {
            margin: 15px 5px;

        }

        .file_row {
            margin: 15px 2px;
        }

        div.disabled {
            pointer-events: none;
            opacity: 0.5;
        }

        .typeBlock {
            span {
                color: $ypo-dark-background-gray;
                font-size: 9px;
                font-weight: bold;
                letter-spacing: 0.59px;
                line-height: 12px;
                display: block;
                text-transform: uppercase;
            }

            i {
                color: $ypo-dark-background-gray;
            }

        }

        .nameBlock {
            strong {
                color: $ypo_blue;
                font-size: 14px;
                font-weight: bold;
                letter-spacing: 0.19px;
                line-height: 26px;
                word-break: break-all;
            }
        }

        .error-message {
            color: red;
            margin: 8px;
        }
        
        .card__file-upload {
            cursor: pointer;

            & label {
                cursor: pointer;
            }
            
        }
        
    }
    
</style>
